/**
 * Bookmark card component for the Bookmarks page
 * Displays a single bookmark with edit/delete actions
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { BookmarkWithBook } from '../../hooks/useAllBookmarks';
import { BOOKMARK_COLORS } from './BookmarkPopover';
import {
  BookOpenIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';

interface BookmarkCardProps {
  bookmark: BookmarkWithBook;
  onUpdate: (bookId: string, bookmarkId: string, data: { label?: string; color?: string }) => Promise<void>;
  onDelete: (bookId: string, bookmarkId: string) => Promise<void>;
}

export default function BookmarkCard({ bookmark, onUpdate, onDelete }: BookmarkCardProps) {
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(bookmark.label || '');
  const [editColor, setEditColor] = useState(bookmark.color);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const colorConfig = BOOKMARK_COLORS.find(c => c.name === bookmark.color) || BOOKMARK_COLORS[0];

  // Navigate to the book at the bookmarked page
  const handleJumpToPage = () => {
    navigate(`/dhyana/${bookmark.book_id}?page=${bookmark.page_number}`);
  };

  // Start editing
  const handleStartEdit = () => {
    setEditLabel(bookmark.label || '');
    setEditColor(bookmark.color);
    setIsEditing(true);
  };

  // Save edit
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdate(bookmark.book_id, bookmark.id, {
        label: editLabel.trim() || undefined,
        color: editColor
      });
      setIsEditing(false);
    } catch (err: any) {
      showToast('error', err.message || 'Failed to update bookmark');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel edit
  const handleCancel = () => {
    setEditLabel(bookmark.label || '');
    setEditColor(bookmark.color);
    setIsEditing(false);
  };

  // Delete bookmark
  const handleDelete = async () => {
    if (!window.confirm('Delete this bookmark?')) return;

    setIsDeleting(true);
    try {
      await onDelete(bookmark.book_id, bookmark.id);
    } catch (err: any) {
      showToast('error', err.message || 'Failed to delete bookmark');
      setIsDeleting(false);
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      className={`group border rounded-xl p-4 transition-colors ${
        isDarkMode
          ? 'bg-slate-900/50 border-slate-700/50 hover:border-slate-600'
          : 'bg-white border-gray-200 hover:border-gray-300'
      } border-l-4`}
      style={{ borderLeftColor: colorConfig.class.replace('bg-', '').includes('blue') ? '#3B82F6' :
               colorConfig.class.includes('red') ? '#EF4444' :
               colorConfig.class.includes('green') ? '#22C55E' :
               colorConfig.class.includes('yellow') ? '#EAB308' :
               colorConfig.class.includes('purple') ? '#A855F7' :
               '#F97316' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <BookmarkSolidIcon className={`w-4 h-4 ${colorConfig.text}`} />
          <span className={`text-sm font-mono ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            Page {bookmark.page_number}
          </span>
        </div>

        {/* Actions */}
        {!isEditing && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={handleJumpToPage}
              className={`p-1.5 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
              title="Open in reader"
            >
              <BookOpenIcon className="w-4 h-4" />
            </button>
            <button
              onClick={handleStartEdit}
              className={`p-1.5 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                isDarkMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-gray-100 text-gray-500'
              }`}
              title="Delete"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Label Display / Edit */}
      {isEditing ? (
        <div className="space-y-3">
          <input
            type="text"
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            placeholder="Add a label (optional)..."
            maxLength={255}
            className={`w-full px-3 py-2 text-sm rounded-lg border focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 ${
              isDarkMode
                ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
            }`}
            autoFocus
          />

          {/* Color picker */}
          <div className="flex items-center gap-2">
            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Color:</span>
            <div className="flex gap-1.5">
              {BOOKMARK_COLORS.map(color => (
                <button
                  key={color.name}
                  onClick={() => setEditColor(color.name)}
                  className={`w-5 h-5 rounded-full ${color.class} ${
                    editColor === color.name ? 'ring-2 ring-offset-2 ring-cyan-500' : ''
                  } ${isDarkMode ? 'ring-offset-slate-900' : 'ring-offset-white'}`}
                />
              ))}
            </div>
          </div>

          {/* Save/Cancel buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
            >
              <CheckIcon className="w-4 h-4" />
              Save
            </button>
            <button
              onClick={handleCancel}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border ${
                isDarkMode
                  ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <XMarkIcon className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          {bookmark.label && (
            <p className={`text-sm mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              {bookmark.label}
            </p>
          )}
          <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
            Added {formatDate(bookmark.created_at)}
          </p>
        </>
      )}
    </div>
  );
}
